/**
 * useProfileComplete — Wave A6 Chunk 2 (Task 2.5).
 *
 * Decides whether to nudge an authenticated user to /complete-profile.
 * A user "needs completion" when their identity row has no first name AND no
 * display name AND no mirrored auth name. The prompt fires at most once per
 * browser session — "skip for now" sets the dismiss flag so the user is never
 * jailed on the page.
 */

import { useAuth } from './useAuth';
import { useUserProfile } from '../context/UserProfileContext';
import { resolveUserDisplayName } from '../utils/userDisplayName';
import { isChildLogin } from '../utils/userRoles';

const DISMISS_KEY = 'kantera_profile_prompt_dismissed';

export function dismissProfilePrompt(): void {
  window.sessionStorage.setItem(DISMISS_KEY, '1');
}

export function isProfilePromptDismissed(): boolean {
  return window.sessionStorage.getItem(DISMISS_KEY) === '1';
}

interface ProfileCompleteState {
  /** True once we know the profile and the user genuinely has no name set. */
  needsCompletion: boolean;
  /** True until the profile fetch resolves — callers must not redirect yet. */
  loading: boolean;
  /** True when the user has any usable name (first, display, or auth name). */
  hasName: boolean;
}

export function useProfileComplete(): ProfileCompleteState {
  const { user, tenant } = useAuth();
  const { profile, loading } = useUserProfile();

  // Deliberately WITHOUT the email fallback: a name derived from an email local
  // part must not count as "the user has told us their name", or this hook would
  // stop prompting anyone. Omitting `email` from the sources expresses exactly
  // that, and picks up the lastName-only case the old check missed.
  const hasName = Boolean(resolveUserDisplayName({ profile, metadataName: user?.name }));

  // Only assert "needs completion" once the profile has loaded — otherwise the
  // first paint (profile still null) would wrongly redirect everyone.
  const resolved = !loading && profile !== null;
  // COPPA: a child's own login is never sent to fill in a name and phone number. Those are its
  // parent's to give (the API refuses them from a child: 403 CHILD_PROFILE_PARENT_MANAGED).
  const childLogin = isChildLogin(tenant?.roles ?? []);
  const needsCompletion = resolved && !hasName && !childLogin && !isProfilePromptDismissed();

  return { needsCompletion, loading: !resolved, hasName };
}
